import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Mail, Building, HandHeart, Users } from "lucide-react";
import { GradientHeading } from "@/components/ui/gradient-heading";
import { PaechterJobs } from "@/components/PaechterJobs";
import { SEOPageMeta } from "@/components/SEOPageMeta";
import { SEOBreadcrumbSchema } from "@/components/SEOBreadcrumbSchema";
import { useNavigate } from "react-router-dom";

const Verpachtung = () => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.querySelector('#contact');
      contactSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  return <div className="min-h-screen bg-background">
      <SEOPageMeta 
        title="Gastronomie Verpachtung & Objektvermittlung"
        description="Professionelle Verpachtung von Hotels & Gastronomie-Objekten. Individuelle Beratung für Verpächter und Pächter. Persönliche Betreuung durch erfahrene Experten."
        path="/verpachtung"
        keywords={["Gastronomie Verpachtung", "Hotel Verpachtung", "Pachtobjekt", "Objektvermittlung Gastronomie", "Gastronomie pachten"]}
      />
      <SEOBreadcrumbSchema items={[
        { name: "Home", path: "/" },
        { name: "Verpachtung", path: "/verpachtung" }
      ]} />
      <Header />
      
      {/* Enhanced Hero Section - Mobile Optimized */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Enhanced Background with Depth */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-elegant opacity-90" />
          
          {/* Layered Background Elements - Mobile Responsive */}
          <div className="absolute top-1/4 right-1/4 w-48 h-48 sm:w-96 sm:h-96 rounded-full blur-3xl opacity-40" style={{
          background: 'radial-gradient(circle, hsl(210 65% 32% / 0.1) 0%, transparent 70%)'
        }} />
          <div className="absolute bottom-1/3 left-1/3 w-32 h-32 sm:w-64 sm:h-64 rounded-full blur-2xl opacity-30" style={{
          background: 'radial-gradient(circle, hsl(210 65% 32% / 0.08) 0%, transparent 70%)'
        }} />
          
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(210 65% 32%) 1px, transparent 0)`,
          backgroundSize: '30px 30px'
        }} />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-8 text-center">
          <div className="max-w-4xl mx-auto animate-fade-in">
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <span className="w-12 sm:w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></span>
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{
                backgroundColor: 'hsl(210 65% 32%)'
              }}></div>
                <span className="w-12 sm:w-16 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></span>
              </div>
              <p className="font-medium tracking-wider uppercase text-xs sm:text-sm mb-4 sm:mb-6" style={{
              color: 'hsl(210 65% 32%)'
            }}>GASTRONOMIE-VERPACHTUNG</p>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-6xl font-display font-extralight mb-6 sm:mb-8 text-foreground leading-tight px-4 sm:px-0">
              <span className="bg-gradient-to-br from-foreground via-foreground to-primary bg-clip-text text-transparent">
                Verpachtung
              </span>
              <span className="block text-2xl sm:text-3xl md:text-4xl font-light mt-2 sm:mt-4" style={{
              color: 'hsl(210 65% 32%)'
            }}>
                & Objektvermittlung
              </span>
            </h1>
            
            <div className="max-w-3xl mx-auto mb-8 sm:mb-12 relative px-4 sm:px-0">
              <div className="hidden sm:block absolute -left-6 top-0 w-1 h-full bg-gradient-to-b from-transparent via-primary to-transparent opacity-30"></div>
              <p className="text-base sm:text-xl text-muted-foreground leading-relaxed font-light">
                Sie möchten ein gastronomisches Objekt verpachten oder durch eine Pacht den Schritt in die Selbständigkeit gehen? 
                Sprechen Sie uns an! Als starker Partner sind wir bei diesen Schritten an Ihrer Seite.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4 sm:px-0">
              <Button 
                variant="default" 
                size="lg" 
                className="font-medium text-base sm:text-lg px-8 sm:px-10"
                onClick={handleContactClick}
              >
                <Mail className="w-4 h-4 mr-2" />
                Kontakt aufnehmen
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="font-medium text-base sm:text-lg px-8 sm:px-10"
                onClick={() => {
                  const pachterSection = document.querySelector('#paechter-jobs');
                  pachterSection?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Pachtobjekt finden
              </Button>
            </div>
          </div>
        </div>

        {/* Enhanced Bottom Shape */}
        <div className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 bg-background organic-shape">
          <div className="absolute inset-0 bg-gradient-accent-subtle opacity-50"></div>
        </div>
      </section>

      {/* Main Content - Mobile Optimized */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start">
            
            {/* Verpachtung inserieren */}
            <Card className="p-6 sm:p-8 bg-card/50 border-border/50 backdrop-blur-sm order-2 lg:order-1">
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="h-10 w-10 sm:h-12 sm:w-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <GradientHeading size="xl">
                  Sie haben ein eigenes Objekt zu verpachten?
                </GradientHeading>
              </div>
              
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4 sm:mb-6">
                Sie möchten ein Hotel oder ein gastronomisches Objekt verpachten? Es ist nicht immer einfach, 
                die passende Pacht-Lösung zu finden. Wo sucht man am besten? Wer eignet sich und 
                welche der zahlreichen Pachtvertragsvarianten ist für das Objekt die richtige? Das sind nur 
                einige der sich stellenden Fragen.
              </p>
              
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 sm:mb-8">
                Mit fundierter Erfahrung in der Hotellerie und Gastronomie sind wir von Scholz Interim Ihr 
                Ansprechpartner. Ihre Betreuung bei der Verpachtung bedeutet für uns nicht, einfach nur eine 
                Pacht-Lösung zu finden. In einem persönlichen Vorgespräch erarbeiten wir zunächst gemeinsam mit Ihnen 
                die Perspektiven für die Verpachtung des Objektes und entwickeln so die für Sie ideale Lösung.
              </p>
              
              <Button 
                variant="hero" 
                size="lg" 
                className="w-full"
                onClick={handleContactClick}
              >
                <Mail className="w-4 h-4 mr-2" />
                <span className="text-sm sm:text-base">Kontakt aufnehmen</span>
              </Button>
            </Card>

            {/* Unser Service */}
            <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
              <GradientHeading size="xl" className="mb-6 sm:mb-8">
                Unser Service für Sie
              </GradientHeading>
              
              <div className="space-y-4 sm:space-y-6">
                <Card className="p-4 sm:p-6 bg-card/30 border-border/30">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <HandHeart className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-display font-light text-foreground mb-2">Persönliche Betreuung</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Diskrete und anonyme Betreuung während des gesamten Verpachtungsprozesses.
                      </p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 sm:p-6 bg-card/30 border-border/30">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-display font-light text-foreground mb-2">Pächter-Potenzial-Analyse</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Gründliche Prüfung potenzieller Interessent:innen vor der ersten Kontaktaufnahme.
                      </p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 sm:p-6 bg-card/30 border-border/30">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Building className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-display font-light text-foreground mb-2">Objektanalyse</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Entwicklung der idealen Verpachtungslösung für Ihr spezifisches Objekt.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pächter Jobs Section */}
      <div id="paechter-jobs">
        <PaechterJobs />
      </div>
      
      <Footer />
    </div>;
};
export default Verpachtung;